import React, { useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import { StylableComponent, stylePropType } from '@adsk/alloy-react-theme';

import TrialModal from './components/TrialModal';
import {
  AUTOSPECS_TRIAL_MARKETING_URL,
  OFFERINGS_CATEGORIES,
  PRICING_URL,
} from './components/TrialModalItem/helpers/const';
import { buttonStyle, containerStyle, messageStyle } from './helpers/styles';
import {
  BannerMessage,
  bannerMessagePropType,
  Offering,
  offeringPropType,
} from './helpers/types';

type TrialBannerViewProps = StylableComponent<HTMLDivElement> & {
  onClickAnalytics: () => void;
  displayButton: boolean | null;
  message: BannerMessage | null;
  userTrials: Offering[];
};

const TrialBannerView = React.forwardRef<HTMLDivElement, TrialBannerViewProps>(
  (
    {
      displayButton,
      message,
      userTrials,
      onClickAnalytics,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const [show, setShow] = useState(false);
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

    const handleClickBuyingOptions = () => {
      const justOneTrial = userTrials && userTrials.length === 1;

      if (!justOneTrial) {
        setShow(true);
      } else {
        const buyingOptionsLink =
          userTrials[0].offeringCategory === OFFERINGS_CATEGORIES.AUTOSPECS
            ? AUTOSPECS_TRIAL_MARKETING_URL
            : `${PRICING_URL}/${userTrials[0].offeringCategory}`;
        window.open(buyingOptionsLink, '_blank');
      }
      onClickAnalytics();
    };

    return (
      <div
        className={className}
        css={[containerStyle, style]}
        ref={ref}
        data-testid="TrialBanner"
        {...props}
      >
        <div css={messageStyle(message ? message.daysLeft : null)}>
          {message ? message.text : ''}
        </div>
        {displayButton && (
          <Button
            data-testid="TrialBanner_button"
            style={buttonStyle}
            variant={Button.VARIANTS.PRIMARY}
            onClick={handleClickBuyingOptions}
          >
            {T.translate('BANNER_BUTTON_LABEL')}
          </Button>
        )}
        <TrialModal
          data-testid="TrialBanner_modal"
          onClose={() => {
            setShow(false);
          }}
          show={show}
          userTrials={userTrials}
        />
      </div>
    );
  }
);

TrialBannerView.displayName = 'TrialBannerView';
TrialBannerView.defaultProps = {
  message: null,
  displayButton: false,
};
TrialBannerView.propTypes = {
  /** Analytics method to run on open modal */
  onClickAnalytics: PropTypes.func.isRequired,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Display button flag */
  displayButton: PropTypes.bool,
  /**  The message to display  */
  message: bannerMessagePropType,
  /** User Trials data */
  userTrials: PropTypes.arrayOf(offeringPropType.isRequired).isRequired,
  /** Styles applied to the root element */
  style: stylePropType,
};
export default TrialBannerView;
